import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Mail, Phone, MapPin, Clock, Send } from 'lucide-react';
import { contactInfo } from '../data/mock';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    toast.success('Thank you! We will contact you soon.');
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-[#0f172a] mb-4">
            Get in <span className="text-[#d4af37]">Touch</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Ready to start your next project? Contact us today for a consultation
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <Card className="shadow-xl border-2 border-gray-200">
            <CardHeader>
              <CardTitle className="text-2xl text-[#0f172a]">Send us a Message</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="John Doe"
                    className="w-full focus:border-[#d4af37] focus:ring-[#d4af37]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="john@example.com"
                    className="w-full focus:border-[#d4af37] focus:ring-[#d4af37]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+1 (555) 123-4567"
                    className="w-full focus:border-[#d4af37] focus:ring-[#d4af37]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project Details *
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Tell us about your project..."
                    rows={5}
                    className="w-full focus:border-[#d4af37] focus:ring-[#d4af37]"
                  />
                </div>

                <Button 
                  type="submit"
                  className="w-full bg-[#d4af37] text-[#0f172a] hover:bg-[#c4a137] font-semibold py-6 text-lg transition-all duration-200 hover:scale-105 group"
                >
                  Send Message
                  <Send className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <div className="space-y-8">
            <Card className="shadow-xl border-2 border-gray-200 hover:border-[#d4af37]/30 transition-colors duration-300">
              <CardContent className="p-8">
                <div className="flex items-start mb-6">
                  <div className="w-12 h-12 bg-[#d4af37]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="text-[#d4af37]" size={24} />
                  </div>
                  <div className="ml-4">
                    <h3 className="font-bold text-[#0f172a] mb-1">Email Us</h3>
                    <a 
                      href={`mailto:${contactInfo.email}`}
                      className="text-gray-600 hover:text-[#d4af37] transition-colors"
                    >
                      {contactInfo.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start mb-6">
                  <div className="w-12 h-12 bg-[#d4af37]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="text-[#d4af37]" size={24} />
                  </div>
                  <div className="ml-4">
                    <h3 className="font-bold text-[#0f172a] mb-1">Call Us</h3>
                    <a 
                      href={`tel:${contactInfo.phone}`}
                      className="text-gray-600 hover:text-[#d4af37] transition-colors"
                    >
                      {contactInfo.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start mb-6">
                  <div className="w-12 h-12 bg-[#d4af37]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-[#d4af37]" size={24} />
                  </div>
                  <div className="ml-4">
                    <h3 className="font-bold text-[#0f172a] mb-1">Visit Us</h3>
                    <p className="text-gray-600">
                      {contactInfo.address}<br />
                      {contactInfo.city}
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#d4af37]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="text-[#d4af37]" size={24} />
                  </div>
                  <div className="ml-4">
                    <h3 className="font-bold text-[#0f172a] mb-1">Business Hours</h3>
                    <p className="text-gray-600">{contactInfo.hours}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* CTA Card */}
            <Card className="bg-gradient-to-br from-[#0f172a] to-[#1a1f3a] border-none shadow-2xl">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-white mb-4">
                  Ready to Build Something <span className="text-[#d4af37]">Extraordinary?</span>
                </h3>
                <p className="text-gray-300 mb-6 leading-relaxed">
                  Let's discuss your project and turn your vision into reality. Our team is ready to provide expert guidance and exceptional service.
                </p>
                <Button 
                  className="w-full bg-[#d4af37] text-[#0f172a] hover:bg-[#c4a137] font-semibold py-6 text-lg"
                  onClick={() => {
                    const form = document.querySelector('form');
                    form?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  Request a Quote
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
